import { useMutation } from "@apollo/client";
import { createFileRoute, Outlet, useMatchRoute, Link } from "@tanstack/react-router";
import { format } from "date-fns";
import { Calendar, Table } from "lucide-react";
import { useState } from "react";
import { BookingForm } from "@/bookings/BookingForm";
import { type BookingFormSchema } from "@/bookings/BookingForm/validation";
import { bookingsChartQuery } from "@/bookings/BookingsChart";
import { supplyChainBookingsQuery } from "@/bookings/BookingsTable";
import { destinationsSummaryQuery } from "@/bookings/FeedyardSummaryDashboard/DestinationsTable";
import {
  sourcesSummaryQuery,
  destinationsTotalsQuery,
  sourcesTotalsQuery,
} from "@/bookings/FeedyardSummaryDashboard/queries";
import { PageHeader } from "@/components/PageHeader";
import { Tabs, TabsList, TabsTrigger } from "@/components/Tabs";
import {
  AlertDialog,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogContent,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { graphql } from "@/graphql";
import { useActiveBusinessUnit } from "@/hooks/use-active-business-unit";
import { useToast } from "@/hooks/use-toast";
import { nullable } from "@/lib/utils/common";
import { extractErrorMessages, getErrorMessage } from "@/lib/utils/errors";

export const Route = createFileRoute("/(authed)/_layout/bookings/_tabbed")({
  component: RouteComponent,
});

function RouteComponent() {
  const matchRoute = useMatchRoute();
  const { activeBusinessUnitId } = useActiveBusinessUnit();
  const { toast } = useToast();
  const [isOpen, setIsOpen] = useState(false);

  const destinationsMatch = matchRoute({
    to: "/bookings/summary",
    search: { view: "destinations" },
    includeSearch: true,
    fuzzy: false,
  });

  const sourcesMatch = matchRoute({
    to: "/bookings/summary",
    search: { view: "sources" },
    includeSearch: true,
    fuzzy: false,
  });

  const bookingsMatch = matchRoute({
    to: "/bookings",
    fuzzy: false,
  });

  const isBookingsSummary = destinationsMatch || sourcesMatch;
  const activeTab = isBookingsSummary ? "summary" : "bookings";

  const [createBooking] = useMutation(createSupplyChainBooking, {
    refetchQueries: () => {
      if (destinationsMatch) {
        return [destinationsTotalsQuery, destinationsSummaryQuery];
      }

      if (sourcesMatch) {
        return [sourcesTotalsQuery, sourcesSummaryQuery];
      }

      if (bookingsMatch) {
        return [supplyChainBookingsQuery, bookingsChartQuery];
      }

      return [];
    },
  });

  const handleSubmit = async (values: BookingFormSchema) => {
    try {
      if (!activeBusinessUnitId) {
        toast({
          title: "Error",
          description: "Missing active business unit.",
          variant: "destructive",
        });

        return;
      }

      const result = await createBooking({
        variables: {
          input: {
            businessUnit: activeBusinessUnitId,
            // Basic booking fields
            name: values.name,
            status: values.status,
            plannedShippingDate: format(values.plannedShipmentDate, "yyyy-MM-dd"),
            numberOfHead: values.numberOfHead,
            note: nullable(values.note),
            destination: nullable(values.destination),
            source: nullable(values.source),
            accountManager: nullable(values.businessAccountManagerId),
            // Metadata fields
            accountNumber: nullable(values.ACCOUNT_NUMBER),
            accreditations: values.ACCREDITATIONS?.length ? values.ACCREDITATIONS : [],
            brandNote: nullable(values.BRAND_NOTE),
            breedNote: nullable(values.BREED_NOTE),
            broker: nullable(values.BROKER),
            certificateNumber: nullable(values.CERTIFICATE_NUMBER),
            confirmed: values.CONFIRMED,
            dateDelivered: values.DATE_DELIVERED ? format(values.DATE_DELIVERED, "yyyy-MM-dd") : null,
            dateOfSale: values.DATE_OF_SALE ? format(values.DATE_OF_SALE, "yyyy-MM-dd") : null,
            estimatedChoicePercent: !!values.ESTIMATED_CHOICE_PERCENT ? Number(values.ESTIMATED_CHOICE_PERCENT) : null,
            estimatedWeightNote: nullable(values.ESTIMATED_WEIGHT_NOTE),
            freightCost: nullable(values.FREIGHT_COST),
            grid: nullable(values.GRID),
            lotNumber: nullable(values.LOT_NUMBER),
            penNumber: nullable(values.PEN_NUMBER),
            price: nullable(values.PRICE),
            program: nullable(values.PROGRAM),
            purchasePricePerCwt: nullable(values.PURCHASE_PRICE_PER_CWT),
            riskManagement: values.RISK_MANAGEMENT,
            serviceProvided: nullable(values.SERVICE_PROVIDED),
            sexClass: nullable(values.SEX_CLASS),
            shippedWeight: !!values.SHIPPED_WEIGHT ? Number(values.SHIPPED_WEIGHT) : null,
            shrinkPercent: !!values.SHRINK_PERCENT ? Number(values.SHRINK_PERCENT) : null,
            slide: values.SLIDE ? Number(values.SLIDE) : null,
            sourceNote: nullable(values.SOURCE_NOTE),
            truckingCompany: nullable(values.TRUCKING_COMPANY),
            veterinarian: nullable(values.VETERINARIAN),
            baseWeight: !!values.BASE_WEIGHT ? Number(values.BASE_WEIGHT) : null,
            labels: values.LABELS?.length ? values.LABELS : [],
            dateOfVisit: values.DATE_OF_VISIT ? format(values.DATE_OF_VISIT, "yyyy-MM-dd") : null,
            condition: nullable(values.CONDITION),
            conditions: values.CONDITIONS?.length ? values.CONDITIONS : [],
            ownershipType: nullable(values.OWNERSHIP_TYPE),
            projectedOutDate: values.PROJECTED_OUT_DATE ? format(values.PROJECTED_OUT_DATE, "yyyy-MM-dd") : null,
          },
        },
      });

      const errors = result.errors || [];

      if (errors.length > 0) {
        toast({
          title: "Error creating booking",
          description: extractErrorMessages(errors),
          variant: "destructive",
        });

        return;
      }

      toast({
        variant: "default",
        title: "Booking created successfully!",
      });

      setIsOpen(false);
    } catch (error: unknown) {
      toast({
        variant: "destructive",
        title: "Uh oh! Something went wrong.",
        description: getErrorMessage(error) || "There was a problem with your request.",
      });
    }
  };

  return (
    <>
      <PageHeader showDivider={false}>
        <div className="w-full space-y-4">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="flex items-center justify-between md:justify-start md:gap-6">
              <PageHeader.Title>Bookings</PageHeader.Title>
              <Button
                variant={"confirm"}
                onClick={() => {
                  setIsOpen(true);
                }}
                className="md:hidden"
              >
                Create Booking
              </Button>
            </div>

            <div className="flex items-center justify-between md:gap-6 w-full md:w-auto">
              <Tabs value={activeTab} className="w-full md:w-auto">
                <TabsList variant="primary" className="w-full md:w-auto">
                  <TabsTrigger asChild variant="primary" value="bookings" className="flex-1 md:flex-none">
                    <Link to="/bookings">
                      <Calendar className="w-4 h-4 mr-2" />
                      Bookings
                    </Link>
                  </TabsTrigger>
                  <TabsTrigger asChild variant="primary" value="summary" className="flex-1 md:flex-none">
                    <Link to="/bookings/summary" search={{ view: "destinations" }}>
                      <Table className="w-4 h-4 mr-2" />
                      Summary
                    </Link>
                  </TabsTrigger>
                </TabsList>
              </Tabs>

              <Button
                variant={"confirm"}
                onClick={() => {
                  setIsOpen(true);
                }}
                className="hidden md:block"
              >
                Create Booking
              </Button>
            </div>
          </div>
        </div>
      </PageHeader>
      <div className="space-y-4">
        <Outlet />
      </div>

      <AlertDialog open={isOpen} onOpenChange={setIsOpen}>
        <AlertDialogContent className="max-w-[800px] max-h-[90vh] overflow-y-auto">
          <AlertDialogHeader>
            <AlertDialogTitle>Create Booking</AlertDialogTitle>
            <AlertDialogDescription className="sr-only">Create Booking Form</AlertDialogDescription>
          </AlertDialogHeader>
          <BookingForm
            onSubmit={handleSubmit}
            onCancel={() => {
              setIsOpen(false);
            }}
          />
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export const createSupplyChainBooking = graphql(`
  mutation CreateBooking($input: CreateSupplyChainBookingInput!) {
    createSupplyChainBooking(input: $input) {
      supplyChainBooking {
        id
        name
        plannedShippingDate
        note
        numberOfHead
        status
        destination {
          id
        }
        source {
          id
        }
      }
    }
  }
`);
